// react
import { ReactNode, useCallback, useContext, useState } from 'react';

// types
import {
	Account,
	AccountBalance,
	AccountEntity,
	Country,
	Loan,
	Transaction,
} from '../../../@types';

// router
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useReimbursements } from '../../../hooks/useReimbursements';
import { useFetch } from '../../../hooks/useFetch';

// icons
import { ModeEditOutlined as IconModeEdit } from '@mui/icons-material';

// context
import { ContextUser } from '../../../App.context';
import { ContextProject, ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// mui
import { Box, ChipProps, Grid, Paper, Typography } from '@mui/material';

// ui
import { Amount } from '../../ui/Amount/Amount';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipBox, ChipBoxProps } from '../../ui/ChipBox/ChipBox';
import { ChipReimbursement } from '../../ui/ChipReimbursement/ChipReimbursement';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { IconCircle, IconCircleProps } from '../../ui/IconCircle/IconCircle';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';
import { VisibilityTextToggle } from '../../ui/VisibilityTextToggle/VisibilityTextToggle';

// links
import { LinkAccount } from '../../links/LinkAccount/LinkAccount';
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';

// utils
import { formatCurrency } from '../../../utils/currency';

// dialogs
import { DialogAddAccountBalance } from '../../dialogs/DialogAddAccountBalance/DialogAddAccountBalance';
import { DialogEditAccount } from '../../dialogs/DialogEditAccount/DialogEditAccount';
import { DialogUpdateAccountStatus } from '../../dialogs/DialogUpdateAccountStatus/DialogUpdateAccountStatus';

// icons
import {
	AddCircleOutlineOutlined as IconAddCircle,
	AddCommentOutlined as IconAddComment,
	ArrowCircleRightOutlined as IconArrowCircleRight,
	DoDisturbOutlined as IconDoDisturb,
	LocalAtmOutlined as IconLocalAtm,
	PaidOutlined as IconPaid,
	PowerSettingsNewOutlined as IconPowerSettingsNew,
	SwapHorizOutlined as IconSwapHoriz,
} from '@mui/icons-material';

// props
export interface CardAccountProps {
	account: Account;
	refetchAccounts?: () => void;
}

export const CardAccount = ({ account, refetchAccounts }: CardAccountProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { totalApplied, totalReimbursed } = useReimbursements({ account });
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogAccount = searchParams.get('dialogAccount');

	// context
	const { isOnboarded: isProjectOnboarded, project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// vars
	const currency = account.currency;

	// state
	const [transaction, setTransaction] = useState<Transaction | null>(null);

	let total = 0;

	if (account.balances.length) {
		total = account.balances.reduce((total, account) => {
			return total + account.balance;
		}, 0);
	}

	const associatedAccounts = account.accounts as Account[];
	const associatedEntities = (account.entities as AccountEntity[]).map((rel) => rel.entity);

	let dateUpdated = null;

	if (account.modified || account.created) {
		dateUpdated = account.modified || account.created;
	}

	if (transaction?.created) {
		if (dateUpdated && new Date(transaction.created) > new Date(dateUpdated)) {
			dateUpdated = transaction.created;
		}
	}

	// fetch latest transaction
	useFetch({
		isEnabled: Boolean(account.id),
		url: `${process.env.REACT_APP_API_URL}/transactions?account=${account.id}&fields=created&orderBy=created&order=DESC&limit=1`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data?.length) {
				setTransaction(res.data[0]);
			}
		},
	});

	// handlers
	const handleOpenDialogAddAccountBalance = useCallback(() => {
		searchParams.set('dialog', 'addAccountBalance');
		searchParams.set('dialogAccount', String(account.id));
		setSearchParams(searchParams);
	}, [account, searchParams, setSearchParams]);

	// button menu items
	const btnMenuItems: MenuItemProps[] = [];

	if (
		userProjectPermissions?.replenishment?.create &&
		(user?.role === 'WFA' || account.type === 'LOCAL')
	) {
		btnMenuItems.push({
			children: t('addToBalance'),
			component: RouterLink,
			icon: <IconAddCircle />,
			onClick: handleOpenDialogAddAccountBalance,
		});
	}

	if (userProjectPermissions?.payment?.create) {
		btnMenuItems.push({
			children: t('recordPayment'),
			component: RouterLink,
			icon: <IconPaid />,
			to: `/project/${project?.idWbg}/transactions/payments?dialog=recordPayment`,
		});
	}

	if (userProjectPermissions?.cashBenefit?.create) {
		btnMenuItems.push({
			children: t('recordCashBenefit'),
			component: RouterLink,
			icon: <IconPaid />,
			to: `/project/${project?.idWbg}/transactions/cash-benefits?dialog=recordCashBenefit`,
		});
	}

	if (userProjectPermissions?.opEx?.create) {
		btnMenuItems.push({
			children: t('recordOpEx'),
			component: RouterLink,
			icon: <IconLocalAtm />,
			to: `/project/${project?.idWbg}/transactions/opex?dialog=recordOpEx`,
		});
	}
	if (userProjectPermissions?.transfer?.create) {
		btnMenuItems.push({
			children: t('recordFundTransfer'),
			component: RouterLink,
			icon: <IconSwapHoriz />,
			to: `/project/${project?.idWbg}/transactions/transfers?dialog=recordFundTransfer`,
		});
	}

	if (userProjectPermissions?.comment?.create) {
		btnMenuItems.push({
			children: t('addComment'),
			component: RouterLink,
			icon: <IconAddComment />,
			to: `/project/${project?.idWbg}/account/${account.id}/comments?dialog=addComment`,
		});
	}

	btnMenuItems.push({
		children: t('seeDetails'),
		component: RouterLink,
		icon: <IconArrowCircleRight />,
		to: `/project/${project?.idWbg}/account/${account.id}`,
	});

	if (userProjectPermissions?.account?.update) {
		btnMenuItems.push({
			children: t('editAccount'),
			icon: <IconModeEdit />,
			onClick: () => {
				searchParams.set('dialog', 'editAccount');
				searchParams.set('dialogAccount', String(account.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.account?.update) {
		let btnLabel = t('deactivateAccount');
		let btnIcon = <IconDoDisturb color="error" />;

		if (account.status === 'DEACTIVATED') {
			btnLabel = t('activateAccount');
			btnIcon = <IconPowerSettingsNew color="success" />;
		}

		btnMenuItems.push({
			children: btnLabel,
			icon: btnIcon,
			onClick: () => {
				searchParams.set('dialog', 'updateAccountStatus');
				searchParams.set('dialogAccount', String(account.id));
				setSearchParams(searchParams);
			},
		});
	}

	const constructBalanceChipProps = (item: AccountBalance) => {
		let color = 'teal';
		let label = t('na');
		let value = t('na');

		if (item.loan) {
			const loan = item.loan as Loan;

			label = loan.code;

			if (['GOV', 'OTH'].includes(loan.financier)) {
				color = 'default';
			}
		}

		if (currency && typeof item.balance === 'number') {
			value = formatCurrency({
				currency,
				value: item.balance,
			});
		}

		return {
			color: color as ChipProps['color'],
			label,
			value,
		};
	};

	const balanceChips: ChipBoxProps[] = [];

	if (account.balances.length) {
		for (let i = 0; i < account.balances.length; i++) {
			// balance loan is a string (code) prior to onboarding
			const loan = account.balances[i].loan;
			const code = typeof loan === 'string' ? (loan as string) : (loan as Loan).code;

			const balanceChipProps = constructBalanceChipProps({
				balance: account.balances[i].balance,
				currency: account.balances[i].currency,
				loan: {
					code,
				},
				transactions: {
					excluded: [],
					included: [],
				},
			});
			balanceChips.push(balanceChipProps);
		}
	} else if (account.loans.length) {
		for (let i = 0; i < account.loans.length; i++) {
			const loan = account.loans[i] as Loan;
			const balanceChipProps = constructBalanceChipProps({
				balance: 0,
				currency: loan.currency,
				loan,
				transactions: {
					excluded: [],
					included: [],
				},
			});
			balanceChips.push(balanceChipProps);
		}
	}

	let address: ReactNode = t('na');

	if (account.street || account.town || account.country) {
		address = (
			<Box component="ul" sx={{ m: 0, p: 0, listStyleType: 'none' }}>
				{account.street && <Box component="li">{account.street}</Box>}
				{account.town && (
					<Box component="li">
						{account.town}
						{account.state ? `, ${account.state}` : ''}
					</Box>
				)}
				{account.postcode && <Box component="li">{account.postcode}</Box>}
				{account.country && <Box component="li">{(account.country as Country)?.name}</Box>}
			</Box>
		);
	}

	const IconCircleProps: IconCircleProps = {
		TooltipProps: {
			title: t('designatedAccount'),
		},
		type: 'account',
	};

	if (account.type === 'LOCAL') {
		IconCircleProps.TooltipProps = {
			title: t('localAccount'),
		};
		if (account.isReimbursement) {
			IconCircleProps.TooltipProps = {
				title: t('reimbursementAccount'),
			};
			IconCircleProps.color = 'warning';
			IconCircleProps.type = 'reimbursement';
		} else {
			IconCircleProps.type = 'local';
		}
	}

	return (
		<Paper sx={{ position: 'relative' }}>
			<Box
				sx={(theme) => {
					return {
						borderBottom: `1px ${theme.palette.grey[300]} solid`,
						padding: theme.spacing(2),
					};
				}}>
				<Grid container spacing={2}>
					<Grid alignSelf="center" item xs={12} lg={2}>
						<Grid alignItems="center" container spacing={2}>
							<Grid item>
								<IconCircle {...IconCircleProps} />
							</Grid>
							<Grid flex={1} item>
								<TypographyCaption id={account.id} />
								<LinkAccount account={account} />
							</Grid>
						</Grid>
					</Grid>

					<Grid item xs={6} md={4} lg={2}>
						<Typography color="brand.grey.500">{t('accountNumber')}</Typography>
						{account.num ? (
							<VisibilityTextToggle text={String(account.num)} />
						) : (
							<Typography>{t('na')}</Typography>
						)}
					</Grid>

					<Grid item xs={6} md={4} lg={2}>
						<Typography color="brand.grey.500">{t('accountHolder')}</Typography>
						<Typography component="span">{account.holder || t('na')}</Typography>
					</Grid>

					<Grid item xs={6} md={4} lg={2}>
						<Typography color="brand.grey.500">{t('lastUpdated')}</Typography>
						<UserAvatarModified
							account={account}
							activityTypes={['ACCOUNT_UPDATED', 'ACCOUNT_CREATED']}
							date={dateUpdated || undefined}
							variant="compact"
						/>
					</Grid>
					<Grid item xs={6} md={4} lg={2}>
						<Typography color="brand.grey.500">{t('balance')}</Typography>
						{currency ? (
							<Amount currency={currency} TypographyProps={{ variant: 'h3' }} value={total} />
						) : (
							<Typography variant="h3">{t('na')}</Typography>
						)}
					</Grid>

					<Grid item xs={6} md={4} lg={1}>
						<Grid container spacing={0.5}>
							<Grid item xs={12}>
								<Typography color="brand.grey.500">{t('status')}</Typography>
							</Grid>
							<Grid item xs={12}>
								<ChipStatus status={account.status} />
							</Grid>
						</Grid>
					</Grid>

					{isProjectOnboarded && (
						<Grid
							item
							xs={true}
							sx={(theme) => {
								return {
									position: 'absolute',
									top: theme.spacing(-1),
									right: theme.spacing(1),
									[theme.breakpoints.up('lg')]: {
										position: 'relative',
										top: 'auto',
										right: 'auto',
									},
								};
							}}>
							{btnMenuItems.length > 0 && (
								<Grid container justifyContent="flex-end">
									<Grid item>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								</Grid>
							)}
						</Grid>
					)}
				</Grid>
			</Box>
			{/* financial institution */}
			<Box
				sx={(theme) => {
					return {
						borderBottom: `1px ${theme.palette.brand.grey[300]} solid`,
						padding: theme.spacing(2),
					};
				}}>
				<Grid container spacing={2}>
					<Grid item xs={6} md={4} lg={2}>
						<Grid container spacing={1}>
							<Grid item>
								<Typography color="brand.grey.500">{t('financialInstitution')}</Typography>
							</Grid>
							<Grid item>
								<IconCircle
									size="xs"
									TooltipProps={{
										title: address,
									}}
									type="location"
								/>
							</Grid>
						</Grid>

						<Typography>{account.institution || t('na')}</Typography>
					</Grid>
					<Grid item xs={6} md={4} lg={2}>
						<Typography color="brand.grey.500">{t('aba')}</Typography>
						<Typography>{account?.aba || t('na')}</Typography>
					</Grid>
					<Grid item xs={6} md={4} lg={2}>
						<Typography color="brand.grey.500">{t('routeCode')}</Typography>
						<Typography>{account?.routeCode || t('na')}</Typography>
					</Grid>
					<Grid item xs={6} md={4} lg={2}>
						<Typography color="brand.grey.500">{t('swiftCode')}</Typography>
						<Typography>{account?.swiftCode || t('na')}</Typography>
					</Grid>
					<Grid item xs={6} md={4} lg={2}>
						<Typography color="brand.grey.500">{t('sortCode')}</Typography>
						<Typography>{account?.sortCode || t('na')}</Typography>
					</Grid>
					{account.isReimbursement && (
						<Grid item xs={6} md={4} lg={2}>
							<Grid container spacing={0.5}>
								<Grid item xs={12}>
									<Typography color="brand.grey.500">{t('applied')}</Typography>
								</Grid>
								<Grid item xs={12}>
									<ChipReimbursement
										amountApplied={totalApplied}
										amountTotal={totalReimbursed}
										currency={account?.currency}
									/>
								</Grid>
							</Grid>
						</Grid>
					)}
				</Grid>
			</Box>

			{/* financing sources */}
			<Box
				sx={(theme) => {
					return {
						borderBottom: `1px ${theme.palette.brand.grey[300]} solid`,
					};
				}}>
				<Grid alignItems="center" container>
					<Grid item xs={12} md={3} lg={2}>
						<Box
							sx={(theme) => {
								return {
									padding: {
										xs: theme.spacing(2, 2, 0, 2),
										md: theme.spacing(2),
									},
								};
							}}>
							<Typography color="brand.grey.500">{t('financingSources')}</Typography>
						</Box>
					</Grid>
					<Grid item xs={12} md={9}>
						<Box
							sx={(theme) => {
								return {
									padding: theme.spacing(2),
								};
							}}>
							<Grid container spacing={2}>
								{balanceChips.length > 0 ? (
									balanceChips.map((chipProps, i) => {
										return (
											<Grid item key={`account-balance-${i}`} xs={12} md="auto">
												<ChipBox {...chipProps} />
											</Grid>
										);
									})
								) : (
									<Grid item xs={12}>
										<Typography>{t('na')}</Typography>
									</Grid>
								)}
							</Grid>
						</Box>
					</Grid>
				</Grid>
			</Box>
			{/* entities and associated accounts */}
			<Box
				sx={(theme) => {
					return {
						padding: theme.spacing(2),
					};
				}}>
				<Grid alignItems="center" container spacing={2}>
					<Grid item xs={12} md={3} lg={2}>
						<Typography color="brand.grey.500">{t('entities')}</Typography>
					</Grid>
					<Grid item xs={12} md={6} lg={8}>
						{associatedEntities.length > 0 ? (
							<Grid container spacing={{ xs: 1, md: 2 }}>
								{associatedEntities.map((entity, i) => {
									return (
										<Grid item key={`account-associated-entity-${i}`} xs={12} md="auto">
											<LinkEntity entity={entity} />
										</Grid>
									);
								})}
							</Grid>
						) : (
							<Typography>{t('na')}</Typography>
						)}
					</Grid>
					{account.type === 'LOCAL' && (
						<Grid item xs={12} md={3} lg={2}>
							<Typography color="brand.grey.500">{t('designatedAccounts')}</Typography>
							{associatedAccounts.length > 0 ? (
								<Grid container spacing={1}>
									{associatedAccounts.map((associatedAccount, i) => {
										const isLast = i === associatedAccounts.length - 1;
										return (
											<Grid item key={`account-associated-account-${i}`}>
												<Typography fontWeight={500}>
													{associatedAccount.name}
													{!isLast ? ',' : ''}
												</Typography>
											</Grid>
										);
									})}
								</Grid>
							) : (
								<Typography>{t('na')}</Typography>
							)}
						</Grid>
					)}
				</Grid>
			</Box>
			{userProjectPermissions?.replenishment?.create && user?.role === 'WFA' && (
				<DialogAddAccountBalance
					account={account}
					isOpen={paramDialog === 'addAccountBalance' && paramDialogAccount === String(account.id)}
					onClose={() => {
						searchParams.delete('dialog');
						searchParams.delete('dialogAccount');
						setSearchParams(searchParams);
					}}
					onSuccess={() => {
						if (refetchAccounts) {
							refetchAccounts();
						}
					}}
				/>
			)}
			{userProjectPermissions?.account?.update && (
				<DialogEditAccount
					isOpen={paramDialog === 'editAccount' && paramDialogAccount === String(account.id)}
					onClose={() => {
						searchParams.delete('dialog');
						searchParams.delete('dialogAccount');
						setSearchParams(searchParams);
					}}
					onSuccess={() => {
						if (refetchAccounts) {
							refetchAccounts();
						}
					}}
				/>
			)}
			{userProjectPermissions?.account?.update && (
				<DialogUpdateAccountStatus
					account={account}
					isOpen={
						paramDialog === 'updateAccountStatus' && paramDialogAccount === String(account.id)
					}
					onClose={() => {
						searchParams.delete('dialog');
						searchParams.delete('dialogAccount');
						setSearchParams(searchParams);
					}}
					onSuccess={() => {
						if (refetchAccounts) {
							refetchAccounts();
						}
					}}
				/>
			)}
		</Paper>
	);
};
